import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const NONE = '__none__';

export interface CariTypeForEdit {
  id: string;
  code: string;
  name: string;
  nameEn: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
  muhasebeAccount: { id: string } | null;
  gelirAccount: { id: string } | null;
  giderAccount: { id: string } | null;
}

interface CariTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cariType?: CariTypeForEdit | null;
  accountOptions: ComboboxOption[];
}

interface AccountFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
}

function AccountField({ label, hint, value, onChange, options }: AccountFieldProps) {
  const { t } = useTranslation('travelCari');
  const withNone: ComboboxOption[] = [{ value: NONE, label: t('cariTypes.noAccount') }, ...options];
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Combobox
        options={withNone}
        value={value}
        onValueChange={onChange}
        placeholder={t('cariTypes.selectAccount')}
        searchPlaceholder={t('cariTypes.searchAccount')}
      />
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

export function CariTypeModal({
  open,
  onOpenChange,
  cariType,
  accountOptions,
}: CariTypeModalProps) {
  const { t } = useTranslation('travelCari');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!cariType;

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [muhasebeAccountId, setMuhasebeAccountId] = useState(NONE);
  const [gelirAccountId, setGelirAccountId] = useState(NONE);
  const [giderAccountId, setGiderAccountId] = useState(NONE);

  useEffect(() => {
    if (!open) return;
    setCode(cariType?.code ?? '');
    setName(cariType?.name ?? '');
    setNameEn(cariType?.nameEn ?? '');
    setSortOrder(cariType ? String(cariType.sortOrder) : '');
    setIsActive(cariType?.isActive ?? true);
    setMuhasebeAccountId(cariType?.muhasebeAccount?.id ?? NONE);
    setGelirAccountId(cariType?.gelirAccount?.id ?? NONE);
    setGiderAccountId(cariType?.giderAccount?.id ?? NONE);
  }, [open, cariType]);

  const utils = trpc.useUtils();
  const invalidate = () => {
    utils.travelCariType.listDetailed.invalidate();
    utils.travelCariType.list.invalidate();
    utils.travelCari.cardCounts.invalidate();
  };

  const createMutation = trpc.travelCariType.create.useMutation({
    onSuccess: () => {
      invalidate();
      showSuccessToast(t('cariTypes.typeCreated'));
      onOpenChange(false);
    },
    onError: (error) => showErrorToast(error.message),
  });

  const updateMutation = trpc.travelCariType.update.useMutation({
    onSuccess: () => {
      invalidate();
      showSuccessToast(t('cariTypes.typeUpdated'));
      onOpenChange(false);
    },
    onError: (error) => showErrorToast(error.message),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const accountFields = {
      muhasebeAccountId: muhasebeAccountId === NONE ? null : muhasebeAccountId,
      gelirAccountId: gelirAccountId === NONE ? null : gelirAccountId,
      giderAccountId: giderAccountId === NONE ? null : giderAccountId,
    };
    const parsedSort = sortOrder.trim() === '' ? undefined : Number(sortOrder);

    if (isEditing && cariType) {
      updateMutation.mutate({
        id: cariType.id,
        name: name.trim(),
        nameEn: nameEn.trim() || null,
        sortOrder: parsedSort,
        isActive,
        ...accountFields,
      });
    } else {
      createMutation.mutate({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        nameEn: nameEn.trim() || null,
        sortOrder: parsedSort,
        isActive,
        ...accountFields,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('cariTypes.editType') : t('cariTypes.newType')}</DialogTitle>
          <DialogDescription>{t('cariTypes.modalDesc')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cari-type-code">{t('cariTypes.code')}</Label>
              <Input
                id="cari-type-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                disabled={isEditing}
                required={!isEditing}
                maxLength={6}
                pattern="[A-Z0-9]{1,6}"
                title={t('cariTypes.codeHint')}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="cari-type-name">{t('cariTypes.name')}</Label>
              <Input
                id="cari-type-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="cari-type-name-en">{t('cariTypes.nameEn')}</Label>
              <Input
                id="cari-type-name-en"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                maxLength={60}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cari-type-sort">{t('cariTypes.sortOrder')}</Label>
              <Input
                id="cari-type-sort"
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
          </div>

          <AccountField
            label={t('cariTypes.muhasebeAccount')}
            hint={t('cariTypes.muhasebeAccountHint')}
            value={muhasebeAccountId}
            onChange={setMuhasebeAccountId}
            options={accountOptions}
          />
          <AccountField
            label={t('cariTypes.gelirAccount')}
            hint={t('cariTypes.gelirAccountHint')}
            value={gelirAccountId}
            onChange={setGelirAccountId}
            options={accountOptions}
          />
          <AccountField
            label={t('cariTypes.giderAccount')}
            hint={t('cariTypes.giderAccountHint')}
            value={giderAccountId}
            onChange={setGiderAccountId}
            options={accountOptions}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cari-type-active"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={cariType?.isSystem && isActive}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="cari-type-active">{t('cariTypes.active')}</Label>
            {cariType?.isSystem && (
              <span className="text-muted-foreground text-xs">{t('cariTypes.systemLocked')}</span>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !name.trim() || (!isEditing && !code.trim())}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
